import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import axios from "axios";
import React, { useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface CompaniesSearchDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  companyId: string;
  listId: string;
  onSuccess?: () => void;
}

const CompaniesSearchDialog: React.FC<CompaniesSearchDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  companyId,
  listId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    organizationLocations: [] as string[],
    organizationNumEmployeesRanges: [] as string[],
    organizationIndustries: [] as string[],
    orgLocationInput: "",
    industryInput: "",
  });

  const employeeRangeOptions = [
    "1-10",
    "11-50",
    "51-200",
    "201-500",
    "501-1000",
    "1001-5000",
    "5001-10000",
    "10001+",
  ];

  const handleAddChip = (field: string, value: string, inputField: string) => {
    if (value.trim()) {
      setSearchParams((prev) => ({
        ...prev,
        [field]: [...(prev as any)[field], value.trim()],
        [inputField]: "",
      }));
    }
  };

  const handleDeleteChip = (field: string, index: number) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: (prev as any)[field].filter((_: any, i: number) => i !== index),
    }));
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      
      const rapidApiParams = {
        organization_locations: searchParams.organizationLocations,
        organization_num_employees_ranges: searchParams.organizationNumEmployeesRanges,
        organization_industry_tag_ids: searchParams.organizationIndustries,
      };

      const response = await axios.post(
        `${hostname}/lists/${listId}/import/companies`,
        {
          companyId,
          searchParams: rapidApiParams,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      eventListener({
        type: "SET_SUCCESS",
        message: `Successfully imported ${response.data.imported} companies`,
      });
      
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      const errorMessage = parseError(err);
      if (err.response?.status === 402) {
        eventListener({
          type: "SET_ERROR",
          error: errorMessage || "Insufficient credits to import companies.",
        });
      } else {
        eventListener({
          type: "SET_ERROR",
          error: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Search Companies
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Search for companies from external data sources. Results will be imported into your smart list.
        </Typography>

        <Alert icon={<InfoIcon />} severity="info" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2">
            Each imported company will cost <strong>1 credit</strong>. Up to 500 companies can be imported per search.<br/>
            <strong>Note:</strong> You must have enough credits for the maximum possible results (500) before the search begins.
          </Typography>
        </Alert>

        <Box sx={{ mt: 3 }}>
          {/* Company Locations */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Company Locations (press Enter to add)"
              value={searchParams.orgLocationInput}
              onChange={(e) =>
                setSearchParams((prev) => ({ ...prev, orgLocationInput: e.target.value }))
              }
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddChip("organizationLocations", searchParams.orgLocationInput, "orgLocationInput");
                }
              }}
              helperText="e.g., California, Texas, United Kingdom, Germany"
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
              {searchParams.organizationLocations.map((location, index) => (
                <Chip
                  key={index}
                  label={location}
                  onDelete={() => handleDeleteChip("organizationLocations", index)}
                />
              ))}
            </Stack>
          </Box>

          {/* Company Size */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="company-size-select-label">Company Size</InputLabel>
              <Select
                labelId="company-size-select-label"
                multiple
                value={searchParams.organizationNumEmployeesRanges}
                label="Company Size"
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    organizationNumEmployeesRanges: e.target.value as string[],
                  }))
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {employeeRangeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option} employees
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Industries */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Industries (press Enter to add)"
              value={searchParams.industryInput}
              onChange={(e) =>
                setSearchParams((prev) => ({ ...prev, industryInput: e.target.value }))
              }
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddChip("organizationIndustries", searchParams.industryInput, "industryInput");
                }
              }}
              helperText="e.g., technology, healthcare, finance, manufacturing"
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
              {searchParams.organizationIndustries.map((industry, index) => (
                <Chip
                  key={index}
                  label={industry}
                  onDelete={() => handleDeleteChip("organizationIndustries", index)}
                />
              ))}
            </Stack>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Note: Leave fields empty to search without filters. At least one search criteria is required.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleSearch}
          loading={loading}
          disabled={
            searchParams.organizationLocations.length === 0 &&
            searchParams.organizationNumEmployeesRanges.length === 0 &&
            searchParams.organizationIndustries.length === 0
          }
        >
          Search & Import
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CompaniesSearchDialog;